import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface DeletionInfo {
  confirmation_code: string;
  status: 'pending' | 'completed';
  created_at: string;
  completed_at: string | null;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:9000/api/v1';

export default function DeletionStatus() {
  const { code } = useParams<{ code: string }>();
  const [info, setInfo] = useState<DeletionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) return;
    fetch(`${API_BASE}/auth/deletion-status/${code}/`)
      .then((r) => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(setInfo)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--vivid-purple)] border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <AlertCircle className="mx-auto mb-4 h-16 w-16 text-red-400" />
        <h1 className="mb-2 text-2xl font-bold" style={{ color: 'var(--vivid-purple)', fontFamily: 'var(--font-primary)' }}>
          Cerere negăsită
        </h1>
        <p className="text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
          Codul de confirmare nu a fost găsit. Verifică link-ul și încearcă din nou.
        </p>
      </div>
    );
  }

  const isCompleted = info?.status === 'completed';

  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center">
      {isCompleted ? (
        <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
      ) : (
        <Clock className="mx-auto mb-4 h-16 w-16 text-amber-500" />
      )}

      <h1 className="mb-2 text-2xl font-bold" style={{ color: 'var(--vivid-purple)', fontFamily: 'var(--font-primary)' }}>
        {isCompleted ? 'Datele au fost șterse' : 'Ștergere în curs'}
      </h1>

      <p className="mb-6 text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        {isCompleted
          ? 'Datele tale personale au fost șterse cu succes din platforma Alba Iulia — Capitala Tineretului din România 2026.'
          : 'Cererea ta de ștergere a datelor este în curs de procesare.'}
      </p>

      <div className="rounded-xl border bg-white p-4 text-left text-sm" style={{ fontFamily: 'var(--font-secondary)' }}>
        <div className="mb-2 flex justify-between">
          <span className="text-muted-foreground">Cod confirmare:</span>
          <span className="font-mono font-medium">{info?.confirmation_code}</span>
        </div>
        <div className="mb-2 flex justify-between">
          <span className="text-muted-foreground">Status:</span>
          <span className={`font-semibold ${isCompleted ? 'text-green-600' : 'text-amber-600'}`}>
            {isCompleted ? 'Finalizat' : 'În așteptare'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Data cererii:</span>
          <span>{info?.created_at ? new Date(info.created_at).toLocaleDateString('ro-RO') : '—'}</span>
        </div>
        {info?.completed_at && (
          <div className="mt-2 flex justify-between">
            <span className="text-muted-foreground">Data finalizării:</span>
            <span>{new Date(info.completed_at).toLocaleDateString('ro-RO')}</span>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
        Pentru întrebări, contactează-ne la{' '}
        <a href="mailto:contact.ctro@albaiulia2026.ro" className="text-[var(--vivid-purple)] underline">
          contact.ctro@albaiulia2026.ro
        </a>
      </p>
    </div>
  );
}
